import { useCallback, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { ChevronDown, Pin, PinOff } from 'lucide-react'
import { Button, cn } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { groupNavEntries, type NavEntry, type NavGroup } from '../nav-registry'
import { appCopy } from '../copy'
import { readUserJson, useStorageScope, writeUserJson } from '@/lib/user-storage'

/**
 * Grouped sidebar (item 61): entries render under collapsible group
 * headers per the central IA table; users pin items into an always-visible
 * "Pinned" section. Pins and collapsed groups persist per device.
 */

// Audit FF-06: names, not keys. The full key is scoped per (company, user) by
// `lib/user-storage` — these were global, so on a shared plant tablet the next
// operator inherited the previous one's pinned modules and collapsed groups.
const PINS_NAME = 'nav.pins'
const COLLAPSED_NAME = 'nav.collapsed'

type NavCopy = ReturnType<typeof useCopy<typeof appCopy.en>>['nav']

/**
 * Nav labels are looked up by ROUTE PATH and fall back to the entry's own
 * English label (see `app/copy.ts`). The fallback is not a nicety: features
 * register their own nav entries, so a new slice appears in the sidebar before
 * anyone adds it to the catalogue, and it must show its name rather than a key
 * or a blank row.
 */
function labelFor(nav: NavCopy, entry: NavEntry): string {
  return (nav.items as Record<string, string | undefined>)[entry.path] ?? entry.label
}

function groupLabelFor(nav: NavCopy, group: NavGroup): string {
  return (nav.groups as Record<string, string | undefined>)[group.key] ?? group.label
}

export function SidebarNav({
  entries,
  expanded,
  onNavigate,
}: {
  entries: readonly NavEntry[]
  /** False when the desktop rail is collapsed to icons. */
  expanded: boolean
  onNavigate?: (() => void) | undefined
}) {
  const c = useCopy(appCopy)
  const scope = useStorageScope()
  const [pins, setPins] = useState<string[]>(() => readUserJson<string[]>(scope, PINS_NAME, []))
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    readUserJson<Record<string, boolean>>(scope, COLLAPSED_NAME, {}),
  )

  const togglePin = useCallback(
    (path: string) => {
      setPins((prev) => {
        const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
        writeUserJson(scope, PINS_NAME, next)
        return next
      })
    },
    [scope],
  )

  const toggleGroup = useCallback(
    (key: string) => {
      setCollapsed((prev) => {
        const next = { ...prev, [key]: !prev[key] }
        writeUserJson(scope, COLLAPSED_NAME, next)
        return next
      })
    },
    [scope],
  )

  // `end` only where another entry nests under this path: without it
  // /maintenance/dispatch lit BOTH Dispatch and Work orders (owner finding,
  // 2026-09-18). Entries with no nested sibling keep prefix matching, so a
  // record page still lights its register.
  const nested = (path: string) => entries.some((other) => other.path !== path && other.path.startsWith(`${path}/`))
  const pinned = entries.filter((entry) => pins.includes(entry.path))
  const groups = groupNavEntries(entries)

  const item = (entry: NavEntry, isPinnedRow: boolean) => {
    const label = labelFor(c.nav, entry)
    return (
      <div key={`${isPinnedRow ? 'pin:' : ''}${entry.path}`} className="group/navitem relative">
        <NavLink
          to={entry.path}
          end={nested(entry.path)}
          onClick={onNavigate}
          title={label}
          // FF-QA-131: the pin is reached with → from its link (and ← back),
          // not as a second Tab stop per item — 30 Tabs to leave one section.
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight' && expanded) {
              event.preventDefault()
              ;(event.currentTarget.nextElementSibling as HTMLElement | null)?.focus()
            }
          }}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-sidebar-accent',
              isActive && 'bg-sidebar-accent text-sidebar-accent-foreground',
            )
          }
        >
          <entry.icon className="h-4 w-4 shrink-0" />
          {expanded && <span className="truncate pr-6">{label}</span>}
        </NavLink>
        {expanded && (
          <Button
            variant="ghost"
            size="inline"
            tabIndex={-1}
            onClick={() => togglePin(entry.path)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') {
                event.preventDefault()
                ;(event.currentTarget.previousElementSibling as HTMLElement | null)?.focus()
              }
            }}
            aria-label={pins.includes(entry.path) ? c.nav.unpin({ label }) : c.nav.pin({ label })}
            className={cn(
              'absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
              pins.includes(entry.path)
                ? 'opacity-70'
                : 'opacity-0 focus-visible:opacity-100 group-hover/navitem:opacity-100',
            )}
          >
            {pins.includes(entry.path) ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {pinned.length > 0 && (
        <div>
          {expanded && (
            <p className="px-3 pb-1 text-[0.65rem] font-semibold uppercase tracking-wider text-sidebar-foreground/70">
              {c.nav.pinned}
            </p>
          )}
          <div className="space-y-1">{pinned.map((entry) => item(entry, true))}</div>
        </div>
      )}
      {groups.map(({ group, items }) => {
        const isCollapsed = expanded && (collapsed[group.key] ?? false)
        return (
          <div key={group.key}>
            {expanded ? (
              <Button
                variant="ghost"
                size="inline"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={!isCollapsed}
                className="w-full justify-between rounded px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/70 hover:bg-transparent hover:text-sidebar-foreground"
              >
                <span>{groupLabelFor(c.nav, group)}</span>
                <ChevronDown className={cn('h-3 w-3 transition-transform', isCollapsed && '-rotate-90')} />
              </Button>
            ) : (
              <div className="mx-3 my-1 border-t border-sidebar-border" />
            )}
            {!isCollapsed && <div className="space-y-1">{items.map((entry) => item(entry, false))}</div>}
          </div>
        )
      })}
    </div>
  )
}
